/**
 * `0:00` / `12:34` / `1:02:03` — the timecode YouTube writes into descriptions and comments, and
 * the unit both the chapter parser and the rich-text linkifier work in.
 *
 * Deliberately narrow. `9:99` is not a timecode, `1:2:3` is not one either, and accepting them
 * would let the chapter parser build a bar out of a phone number or a bible verse.
 *
 * R1: no `Date`, no `Intl`. Digits and arithmetic.
 */

/**
 * Source text, not a `RegExp`, because the linkifier needs it inside a sticky matcher and the
 * chapter parser needs it anchored. Sharing the *pattern* keeps the two in step; sharing a
 * `RegExp` object would share its `lastIndex` too.
 */
export const TIMECODE_PATTERN = '(?:\\d{1,3}:)?\\d{1,2}:\\d{2}';

const TIMECODE_EXACT = new RegExp(`^${TIMECODE_PATTERN}$`);

/**
 * How far past the end of the video a written timecode may stand and still be read as pointing
 * *at* the video — the counterpart, at the far end, of the chapter parser's opening-half-minute
 * allowance.
 *
 * An uploader stamps the description against the edit, and a final trim leaves the last stamp
 * standing a few seconds past the cut: `/watch?v=MLvo2uylstg` (491.5 s of footage, reported as
 * 492) closes its six clean chapter lines with `08:17 - Outro`, five seconds after the video
 * ends. Half a minute covers that shape, while a tracklist or a programme schedule — which
 * overshoots by minutes or reads as a time of day — stays refused exactly as before.
 */
export const MAX_END_OVERRUN_SEC = 30;

/**
 * Seconds, or `null` when the text is not a timecode.
 *
 * Rejects an out-of-range seconds or minutes field rather than normalising it: `1:75` is a typo or
 * a score line, and silently reading it as 135 s puts a chapter marker where nothing happens.
 */
export function parseTimecodeSeconds(text: string): number | null {
  if (!TIMECODE_EXACT.test(text)) return null;

  const parts = text.split(':');
  if (parts.length === 2) {
    const minutes = Number(parts[0]);
    const seconds = Number(parts[1]);
    if (seconds > 59) return null;
    return minutes * 60 + seconds;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  const seconds = Number(parts[2]);
  if (minutes > 59 || seconds > 59) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * The `t=` parameter of a `/watch` link. Invidious writes plain seconds (`t=680`, alongside its
 * own `data-jump-time`), but YouTube's own links use `1h2m3s`, and a description can contain
 * either — the anchor is only Invidious's when Invidious made it.
 */
export function parseTimeParamSeconds(value: string): number | null {
  const text = value.trim();
  if (text === '') return null;

  if (/^\d+$/.test(text)) return Number(text);

  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i.exec(text);
  if (!match) return null;
  const [, h, m, s] = match;
  if (h === undefined && m === undefined && s === undefined) return null;
  return Number(h ?? 0) * 3600 + Number(m ?? 0) * 60 + Number(s ?? 0);
}
